import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { map, switchMap, mergeMap, filter, take, tap, catchError, of } from 'rxjs';
import * as DatasetActions from './google-sheet.actions';
import { GoogleSheetClientService } from './google-sheet-client.service';
import { SocketService, SocketSubscription } from '@mode/shared/data-access-socket';
import { ToastService } from '@mode/shared/util-toast';
import {
  GoogleSheetIngestProgressComponent,
  GoogleSheetIngestSuccessComponent,
  GoogleSheetIngestFailedComponent,
} from '@mode/accounts/util';
import { Store } from '@ngrx/store';
import { progressToastId } from './google-sheet.selectors';
import { GoogleSheetRunEvents, GoogleSheetRunPusherSuccessPayload } from '@mode/dataset/google/contract';
import { DataViewFacade } from '@mode/switch-views/contract';
import { AccountFacade } from '@mode/accounts/contract';
import { GoogleSheetFacade } from '@mode/dataset/contract';
import { OrgTypes } from '@mode/shared/contract-common';

@Injectable()
export class GoogleSheetEffects {
  createGoogleSheetDataset$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DatasetActions.createGoogleSheetDataset),
      switchMap((action) =>
        this.datasetService
          .createGoogleSheetDataset(
            action.tab,
            action.name,
            action.description,
            action.spreadsheetId,
            action.worksheetId,
            action.url,
            action.accountId,
            action.token,
            action.layout
          )
          .pipe(
            map((response) => {
              return DatasetActions.createGoogleSheetDatasetSuccess({
                reportRunToken: response.token,
                channelName: response.pusher_channel_name,
              });
            }),
            catchError(() => {
              return of(DatasetActions.createGoogleSheetDatasetFailed());
            })
          )
      )
    )
  );

  createGoogleSheetDatasetSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DatasetActions.createGoogleSheetDatasetSuccess),
      map(({ reportRunToken, channelName }) => {
        let googleSheetSubscription = {} as SocketSubscription<GoogleSheetRunPusherSuccessPayload>;
        try {
          googleSheetSubscription = this.socketService.connect<GoogleSheetRunPusherSuccessPayload>(channelName);
        } catch (error: any) {
          error.reportRunToken = reportRunToken;
          throw error;
        }
        return DatasetActions.googleSheetSocketConnectionSuccess({ reportRunToken, googleSheetSubscription });
      })
    )
  );

  createGoogleSheetDatasetFailed$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(DatasetActions.createGoogleSheetDatasetFailed),
        tap(() => this.toastService.error('Dataset creation failed. Please try again'))
      ),
    { dispatch: false }
  );

  googleSheetSocketConnectionSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DatasetActions.googleSheetSocketConnectionSuccess),
      mergeMap(({ reportRunToken, googleSheetSubscription }) =>
        googleSheetSubscription.eventBus.pipe(
          filter((event) => Object.values(GoogleSheetRunEvents).includes(event.name as GoogleSheetRunEvents)),
          map((event) => {
            switch (event.name) {
              case GoogleSheetRunEvents.GoogleSheetSuccess:
                googleSheetSubscription.disconnect();
                return DatasetActions.ingestGoogleSheetSuccess({
                  token: reportRunToken,
                  datasetToken: event.data.dataset_report_token,
                  editDatasetLink: event.data._links.web_edit_dataset.href,
                });
              case GoogleSheetRunEvents.GoogleSheetFailed:
                googleSheetSubscription.disconnect();
                return DatasetActions.ingestGoogleSheetFailed({
                  token: reportRunToken,
                  editDatasetLink: event.data._links.web_edit_dataset.href,
                });
            }
            return DatasetActions.ingestGoogleSheetEnqueued({ reportRunToken });
          })
        )
      )
    )
  );

  ingestGoogleSheetEnqueued$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DatasetActions.createGoogleSheetDatasetSuccess),
      map(({ reportRunToken }) => ({
        reportRunToken,
        activeToast: this.toastService.info<unknown, GoogleSheetIngestProgressComponent>('', {
          disableTimeout: true,
          tapToDismiss: false,
          toastComponent: GoogleSheetIngestProgressComponent,
        }),
      })),
      map(({ reportRunToken, activeToast }) =>
        DatasetActions.ingestGoogleSheetInProgresss({ token: reportRunToken, progressToastId: activeToast.toastId })
      )
    )
  );

  ingestGoogleSheetSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(DatasetActions.ingestGoogleSheetSuccess),
        mergeMap(({ token, datasetToken, editDatasetLink }) => {
          return this.store.select(progressToastId(token)).pipe(
            take(1),
            tap((toastId) => {
              if (toastId) {
                this.toastService.remove(toastId);
              }
              this.toastService
                .success('', {
                  closeButton: true,
                  disableTimeout: true,
                  tapToDismiss: false,
                  toastComponent: GoogleSheetIngestSuccessComponent,
                  payload: { editDatasetLink },
                })
                .onAction.subscribe(() => {
                  this.store.dispatch(DatasetActions.afterIngestGoogleSheetSuccess({ datasetToken }));
                });
            })
          );
        })
      ),
    { dispatch: false }
  );

  afterIngestGoogleSheetSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(DatasetActions.afterIngestGoogleSheetSuccess),
        switchMap(({ datasetToken }) => this.accountFacade.currentOrg$.pipe(map((org) => ({ org, datasetToken })))),
        take(1),
        tap(({ org }) => this.loadUserDefaultColorPaletteForChartCreation(org)),
        switchMap(({ org, datasetToken }) =>
          this.dataViewFacade.getDatasetWithSwitchViewToken(org.username, datasetToken)
        ),
        tap((dataset) => this.googleSheetFacadeService.importDataset(dataset))
      ),
    { dispatch: false }
  );

  ingestGoogleSheetFailed$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(DatasetActions.ingestGoogleSheetFailed),
        switchMap(({ token, editDatasetLink }) => {
          return this.store.select(progressToastId(token)).pipe(
            take(1),
            tap((toastId) => {
              if (toastId) {
                this.toastService.remove(toastId);
              }
              this.toastService.warn('', {
                disableTimeout: true,
                tapToDismiss: false,
                toastComponent: GoogleSheetIngestFailedComponent,
                payload: { editDatasetLink },
              });
            })
          );
        })
      ),
    { dispatch: false }
  );

  constructor(
    private actions$: Actions,
    private datasetService: GoogleSheetClientService,
    private socketService: SocketService,
    private toastService: ToastService,
    private store: Store,
    private dataViewFacade: DataViewFacade,
    private accountFacade: AccountFacade,
    private googleSheetFacadeService: GoogleSheetFacade
  ) {}

  private loadUserDefaultColorPaletteForChartCreation(org: OrgTypes.Organization) {
    return this.accountFacade.loadReportOwner(org.username);
  }
}
